import { Image } from '@unpic/react'
import { Star, BookOpen } from 'lucide-react'
import { useTranslations } from 'use-intl'

import { LocalizedLink } from '~/components/common/localized-link'
import { FavoriteButton } from '~/features/favorites/components/FavoriteButton'
import { RankingBadge } from '~/features/rankings/components/RankingBadge'

interface Restaurant {
  id: string
  name: string
  rating: number
  area: string
  distance: string
  category: string
  priceLevel: string
  imageUrl: string
  isHyakumeiten?: boolean
  awards?: {name: string, snapshotId?: string, url?: string}[]
  hasMenu?: boolean
}

interface RestaurantCardProps {
  restaurant: Restaurant
  isSelected?: boolean
  onPress?: () => void
  onHover?: (id: number | null) => void
}

export function RestaurantCard({ restaurant, isSelected, onPress, onHover }: Readonly<RestaurantCardProps>) {
  const t = useTranslations('discovery')

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onPress}
      onMouseEnter={() => onHover?.(Number(restaurant.id))}
      onMouseLeave={() => onHover?.(null)}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          if (onPress) onPress()
        }
      }}
      className={`
        w-full mb-3 transition-[background-color,transform,box-shadow,border-color] duration-200 cursor-pointer rounded-xl overflow-hidden flex flex-col
        outline-none focus-visible:ring-2 focus-visible:ring-primary
        ${isSelected
          ? 'bg-primary/5 dark:bg-primary/10 ring-2 ring-primary shadow-md scale-[1.01]'
          : 'bg-white dark:bg-stone-900 hover:bg-stone-50 dark:hover:bg-stone-800/80 shadow-sm hover:shadow-md'
        }
      `}
      data-testid={`restaurant-card-${restaurant.id}`}
      data-selected={isSelected ? 'true' : 'false'}
      aria-current={isSelected ? 'true' : undefined}
    >
      {/* 主体点击区域：触发地图选中 */}
      <div className="p-3 flex-row gap-4 flex items-stretch">
        <div className="relative shrink-0 block active:scale-95 transition-transform">
          <Image
            alt={restaurant.name}
            className="object-cover rounded-xl"
            src={restaurant.imageUrl}
            layout="fixed"
            width={80}
            height={80}
          />
          {/* Selected indicator dot */}
          {isSelected && (
            <div className="absolute -top-1 -right-1 w-3 h-3 bg-primary rounded-full border-2 border-white dark:border-stone-900 shadow-sm" />
          )}
        </div>

        {/* Content Details */}
        <div className="flex flex-col justify-start py-0.5 flex-1 min-w-0">
          <div className="flex justify-between items-start gap-2">
            <h3 className={`font-bold text-base leading-tight font-['Inter'] line-clamp-2 ${
              isSelected ? 'text-primary dark:text-primary' : 'text-stone-900 dark:text-stone-100'
            }`}>
              {restaurant.name}
            </h3>
            {/* Right Action Icons */}
            <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
              {restaurant.hasMenu && (
                <LocalizedLink
                  to="/shops/$shopId"
                  params={{ shopId: String(restaurant.id) }}
                  hash="menu"
                  className="flex min-h-11 min-w-11 items-center justify-center rounded-full text-stone-400 transition-colors hover:bg-stone-100 hover:text-primary dark:hover:bg-stone-800"
                  title={t('viewMenu', { defaultValue: 'View menu' })}
                  aria-label={t('viewMenu', { defaultValue: 'View menu' })}
                  onClick={(e) => e.stopPropagation()}
                >
                  <BookOpen className="w-4 h-4" />
                </LocalizedLink>
              )}
              <FavoriteButton shopId={Number(restaurant.id)} className="h-11 min-w-11" />
              {/* Rating Badge */}
              <div className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold ${
                isSelected
                  ? 'bg-primary/10 text-primary'
                  : 'bg-stone-100 dark:bg-stone-800 text-stone-800 dark:text-stone-200'
              }`}>
                <Star className="w-3 h-3 text-primary fill-current" aria-hidden="true" />
                {restaurant.rating}
              </div>
            </div>
          </div>

          <p className="text-xs text-stone-500 dark:text-stone-400 mt-1 font-['Noto_Sans_JP'] truncate">
            {restaurant.area}
            {restaurant.area && restaurant.distance && ' • '}
            {restaurant.distance}
          </p>

          <div className="flex items-center gap-2 mt-2 overflow-hidden">
            {restaurant.awards && restaurant.awards.length > 0 ? (
              <div className="flex gap-1 shrink-0 py-1">
                {restaurant.awards.slice(0, 3).map((award, i) => (
                  <RankingBadge key={i} award={award} size="sm" />
                ))}
                {restaurant.awards.length > 3 && (
                  <div className="flex items-center justify-center w-8 h-8 shrink-0 self-center rounded-full bg-stone-100 dark:bg-stone-800 text-[10px] font-bold text-stone-500 shadow-sm border border-stone-200 dark:border-stone-700">
                    +{restaurant.awards.length - 3}
                  </div>
                )}
              </div>
            ) : restaurant.isHyakumeiten ? (
              <div className="shrink-0 py-1">
                <RankingBadge award="百名店" size="sm" />
              </div>
            ) : null}
            {restaurant.category && (
              <span className={`text-[10px] font-medium px-2 py-0.5 rounded truncate shrink min-w-0 ${
                isSelected
                  ? 'bg-primary/10 text-primary'
                  : 'bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300'
              }`}>
                {restaurant.category}
              </span>
            )}
            {restaurant.priceLevel && (
              <span className="text-[10px] font-bold text-stone-500 tracking-wider shrink-0 ml-auto">
                {restaurant.priceLevel}
              </span>
            )}
          </div>
          <LocalizedLink
            to="/shops/$shopId"
            params={{ shopId: String(restaurant.id) }}
            className="mt-2 inline-flex min-h-11 w-fit items-center rounded-full px-3 text-xs font-semibold text-primary transition-colors hover:bg-primary/10"
            onClick={(e) => e.stopPropagation()}
          >
            {t('viewDetails')}
          </LocalizedLink>
        </div>
      </div>
    </div>
  )
}
